// @ts-check

/**
 * @typedef {Object} FfNavigationTabCustomProperties
 * @property {(navigationTabs) => Promise<void>} ffInit
 * @property {Boolean} ffIsActive
 * @property {Function} ffToggleContentsVisibility
 * @property {Function} ffHideContents
 * @property {Function} ffShowContents
 * @typedef {FfNavigationTabCustomProperties & HTMLElement} FfNavigationTab
 */
if (!customElements.get("ff-navigation-tab")) {
  class FfNavigationTab extends HTMLElement {
    /**
     * @type {Boolean}
     */
    ffIsActive;
    constructor() {
      super();

      const { ffContentSelector } = this.dataset;

      if (ffContentSelector) {
        this.ffContentSelector = ffContentSelector;
      } else {
        throw new Error("Missing content selector");
      }
    }

    /**
     * The scope to hide or show contents is limited to FfNavigationTabs
     * @param {FfNavigationTabs} navigationTabs
     */
    ffInit(navigationTabs) {
      /**
       * @type {NodeListOf<HTMLElement>}
       */
      this.ffContents = navigationTabs.querySelectorAll(this.ffContentSelector);

      if (this.ffContents.length == 0)
        throw new Error(
          `No contents for ${this.ffContentSelector} were found.`
        );

      if (this.dataset.ffInitialActive == "true") {
        this.ffShowContents();
      } else {
        this.ffHideContents();
      }
    }

    ffToggleContentsVisibility = () => {
      if (this.ffIsActive) {
        this.ffHideContents();
      } else {
        this.ffShowContents();
      }
    };

    ffHideContents = () => {
      this.ffIsActive = false;

      const { ffActiveClass, ffShowClass, ffHideClass } = this.dataset;

      if (ffActiveClass) {
        this.classList.remove(ffActiveClass);
      }

      this.ffContents?.forEach((content) => {
        if (ffShowClass) {
          content.classList.remove(ffShowClass);
        }

        if (ffHideClass) {
          content.classList.add(ffHideClass);
        }

        if (!ffHideClass && !ffShowClass) {
          content.style.display = "none";
        }
      });
    };

    ffShowContents = () => {
      this.ffIsActive = true;
      const { ffActiveClass, ffShowClass, ffHideClass } = this.dataset;

      if (ffActiveClass) {
        this.classList.add(ffActiveClass);
      }

      this.ffContents?.forEach((content) => {
        if (ffHideClass) {
          content.classList.remove(ffHideClass);
        }

        if (ffShowClass) {
          content.classList.add(ffShowClass);
        }

        if (!ffShowClass && !ffHideClass) {
          content.style.display = "";
        }
      });
    };
  }

  customElements.define("ff-navigation-tab", FfNavigationTab);

  class FfClassesToggler extends FfNavigationTab {
    constructor() {
      super();

      this.ffInit();
    }

    ffInit() {
      /**
       * @type {NodeListOf<HTMLElement>}
       */
      this.ffContents = document.querySelectorAll(this.ffContentSelector);
      this.addEventListener("click", this.ffToggleContentsVisibility);

      if (this.dataset.ffInitialActive == "true") {
        this.ffShowContents();
      } else {
        this.ffHideContents();
      }
    }
  }

  customElements.define("ff-classes-toggler", FfClassesToggler);
}

/**
 * @typedef {Object} FfNavigationTabsCustomProperties
 * @property {() => Promise<void>} ffInit
 * @property {Function} ffHideDefaultContents
 * @typedef {FfNavigationTabsCustomProperties & HTMLElement} FfNavigationTabs
 */
if (!customElements.get("ff-navigation-tabs")) {
  class FfNavigationTabs extends HTMLElement {
    constructor() {
      super();

      this.ffInit();
    }

    ffInit() {
      if (this.dataset.ffDisabled != "true") {
        if (this.dataset.ffDefaultContentSelector) {
          /**
           * @type {NodeListOf<HTMLElement>}
           */
          this.ffDefaultContents = this.querySelectorAll(
            this.dataset.ffDefaultContentSelector
          );
        }

        /**
         * @type {NodeListOf<FfNavigationTab>}
         */
        const tabsEl = this.querySelectorAll(`[data-ff-tab='${this.id}']`);

        if (tabsEl.length == 0)
          throw new Error("No tabs for this navigation tabs were found.");

        //Limits only one tab to be active on initialization removing the data-ff-initial-active
        let foundActiveTab = false;

        tabsEl.forEach((tabEl) => {
          if (tabEl.dataset.ffInitialActive == "true") {
            if (foundActiveTab == true) {
              delete tabEl.dataset.ffInitialActive;
            }
            foundActiveTab = true;
          }

          tabEl.ffInit(this);
          const { ffCanToggle } = tabEl.dataset;

          if (ffCanToggle == "true") {
            tabEl.addEventListener("click", () => {
              const { ffIsActive } = tabEl; //We keep the record before being hidden for the toggle
              tabsEl.forEach((tabEl) => tabEl.ffHideContents());
              tabEl.ffIsActive = ffIsActive; //Record value restored
              this.ffHideDefaultContents();
              tabEl.ffToggleContentsVisibility();
            });
          } else {
            tabEl.addEventListener("click", () => {
              tabsEl.forEach((tabEl) => tabEl.ffHideContents());
              this.ffHideDefaultContents();
              tabEl.ffShowContents();
            });
          }
        });

        if (foundActiveTab) {
          this.ffHideDefaultContents();
        }
      }
    }

    ffHideDefaultContents() {
      this.ffDefaultContents?.forEach((content) => {
        content.remove();
      });
    }
  }

  customElements.define("ff-navigation-tabs", FfNavigationTabs);
}

if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", () => {
    requestAnimationFrame(() => {
      document.querySelectorAll("ff-navigation-tabs").forEach((tabs) => {
        tabs.ffInit();
      });
    });
  });
}
